import type { Entry } from "../../i18n/context.js";
import { parseLsBlocks, type LsBlock, type LsBlockKind } from "../../ls/parse-blocks.js";

export type PresentationTemplate =
  | "scene"
  | "dialogue"
  | "narration"
  | "character"
  | "choice"
  | "moment"
  | "state"
  | "advanced";

export interface PresentationStep {
  readonly id: string;
  readonly line: number;
  readonly depth: number;
  readonly concurrent: boolean;
  readonly kind: LsBlockKind;
  readonly template: PresentationTemplate;
  readonly label: Entry;
  readonly summary: string;
  readonly editable: boolean;
  readonly block: LsBlock;
  /** Concurrent staging directives visually folded into this storyboard frame. */
  readonly companionCount?: number;
}

export type StoryboardSectionKind = "scene" | "dialogue" | "choice" | "moment" | "interaction";

export interface StoryboardSection {
  readonly id: string;
  readonly kind: StoryboardSectionKind;
  readonly label: Entry;
  readonly summary: string;
  readonly startLine: number;
  readonly endLine: number;
  readonly frameCount: number;
  readonly frames: readonly PresentationStep[];
}

export interface StoryboardBranchOption {
  readonly line: number;
  readonly id: string;
  readonly mode: "safe" | "brave";
  readonly text: string;
  readonly outcomes: readonly string[];
  readonly hasCheckDependentOutcomes: boolean;
}

export interface StoryboardExitRule {
  readonly line: number;
  readonly condition: string | null;
  readonly action: "next" | "end";
  readonly target: string;
}

export interface StoryboardBranch {
  readonly choiceLine: number;
  readonly options: readonly StoryboardBranchOption[];
  /** Episode-level exits are deliberately separate from local option outcomes. */
  readonly exits: readonly StoryboardExitRule[];
}

const SECTION_LABELS: Record<StoryboardSectionKind, Entry> = {
  scene: { zh: "场景", en: "Scene" },
  dialogue: { zh: "对白段", en: "Dialogue" },
  choice: { zh: "选择", en: "Choice" },
  moment: { zh: "关键画面", en: "Key moment" },
  interaction: { zh: "互动", en: "Interaction" },
};

const LABELS: Record<LsBlockKind, Entry> = {
  episode: { zh: "章节", en: "Episode" },
  bg: { zh: "背景", en: "Background" },
  char: { zh: "角色", en: "Character" },
  dialogue: { zh: "对白", en: "Dialogue" },
  narration: { zh: "旁白", en: "Narration" },
  cg: { zh: "关键画面", en: "Key visual" },
  music: { zh: "音乐", en: "Music" },
  sfx: { zh: "音效", en: "Sound effect" },
  pause: { zh: "停顿", en: "Pause" },
  choice: { zh: "选择", en: "Choice" },
  option: { zh: "选项", en: "Option" },
  check: { zh: "检定", en: "Check" },
  if: { zh: "条件", en: "Condition" },
  gate: { zh: "路线", en: "Routes" },
  affection: { zh: "好感", en: "Affection" },
  signal: { zh: "状态", en: "Signal" },
  trick: { zh: "互动", en: "Interaction" },
  phone: { zh: "手机", en: "Phone" },
  phoneText: { zh: "消息", en: "Message" },
  minigame: { zh: "小游戏", en: "Minigame" },
  achievement: { zh: "成就", en: "Achievement" },
  butterfly: { zh: "记忆", en: "Memory" },
  param: { zh: "参数", en: "Parameter" },
  unknown: { zh: "高级源码", en: "Advanced source" },
};

const EDITABLE_KINDS = new Set<LsBlockKind>([
  "episode",
  "bg",
  "char",
  "dialogue",
  "narration",
  "cg",
  "music",
  "sfx",
  "pause",
  "option",
  "affection",
  "signal",
  "trick",
  "phoneText",
  "minigame",
  "butterfly",
  "param",
  "gate",
  "if",
]);

function presentationTemplateFor(kind: LsBlockKind): PresentationTemplate {
  switch (kind) {
    case "bg":
    case "music":
    case "sfx":
    case "pause":
      return "scene";
    case "dialogue":
      return "dialogue";
    case "narration":
      return "narration";
    case "char":
      return "character";
    case "choice":
    case "option":
    case "check":
      return "choice";
    case "cg":
    case "trick":
    case "phone":
    case "phoneText":
    case "minigame":
      return "moment";
    case "affection":
    case "signal":
    case "butterfly":
    case "param":
      return "state";
    default:
      return "advanced";
  }
}

function compact(value: string | undefined, fallback = "—"): string {
  const normalized = value?.trim();
  return normalized ? normalized : fallback;
}

function summaryFor(block: LsBlock): string {
  switch (block.kind) {
    case "episode":
      return [block.branchKey, block.title].filter(Boolean).join(" · ") || "Untitled episode";
    case "bg":
      return [compact(block.name), block.transition].filter(Boolean).join(" · ");
    case "char":
      return [compact(block.character), block.bubble ? "bubble" : block.pose, block.transition].filter(Boolean).join(" · ");
    case "dialogue":
    case "narration":
      return `${block.speaker} · ${compact(block.text)}`;
    case "cg":
      return [block.name, block.content].filter(Boolean).join(" · ") || "Key visual";
    case "music":
      return block.stop ? "Stop music" : compact(block.name);
    case "sfx":
      return compact(block.name);
    case "pause":
      return "Tap to continue";
    case "choice":
      return "Choice group";
    case "option":
      return [block.id, block.mode, block.text].filter(Boolean).join(" · ") || "Choice option";
    case "check":
      return "Attribute check";
    case "if":
      return [block.condition ? `if ${block.condition}` : "else", block.next ? `Next · ${block.next}` : block.end ? `End · ${block.end}` : undefined].filter(Boolean).join(" · ");
    case "gate":
      return block.next ? `Next · ${block.next}` : block.end ? `End · ${block.end}` : "Route table";
    case "affection":
      return [block.character, block.delta].filter(Boolean).join(" ") || "Affection change";
    case "signal":
      return [block.signalKind, block.name, block.op, block.value].filter(Boolean).join(" ") || "Story state";
    case "trick":
      return [block.trickType, block.prompt].filter(Boolean).join(" · ") || "Interaction";
    case "phone":
      return "Phone conversation";
    case "phoneText":
      return [block.direction, block.character, block.text].filter(Boolean).join(" · ") || "Message";
    case "minigame":
      return [block.name, block.description].filter(Boolean).join(" · ") || "Minigame";
    case "achievement":
      return compact(block.id, "Achievement");
    case "butterfly":
      return compact(block.description, "Player memory");
    case "param":
      return `${block.key}: ${block.value}`;
    case "unknown":
      return compact(block.raw, "Advanced source");
  }
}

function hash(value: string): string {
  let current = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    current ^= value.charCodeAt(index);
    current = Math.imul(current, 0x01000193);
  }
  return (current >>> 0).toString(36);
}

export function buildPresentationSteps(source: string): PresentationStep[] {
  return parseLsBlocks(source).map((block) => ({
    id: `${block.line}:${block.kind}:${hash(block.raw)}`,
    line: block.line,
    depth: block.depth,
    concurrent: block.concurrent === true,
    kind: block.kind,
    template: presentationTemplateFor(block.kind),
    label: LABELS[block.kind],
    summary: summaryFor(block),
    editable: EDITABLE_KINDS.has(block.kind),
    block,
  }));
}

const STORYBOARD_KINDS = new Set<LsBlockKind>([
  "bg", "char", "dialogue", "narration", "cg", "music", "sfx", "pause",
  "choice", "trick", "phone", "phoneText", "minigame",
]);

export function isStoryboardKind(kind: LsBlockKind): boolean {
  return STORYBOARD_KINDS.has(kind);
}

/**
 * Collapse source-oriented steps into the frames a player can actually see.
 * Choice internals, route/state plumbing, headers, and `&` followers remain in
 * source and the inspector but do not masquerade as separate PPT slides.
 */
export function buildStoryboardFrames(steps: readonly PresentationStep[]): PresentationStep[] {
  const contentDepth = steps.find((step) => step.kind === "episode")?.depth;
  const frameDepth = contentDepth == null
    ? Math.min(...steps.filter((step) => step.kind !== "episode").map((step) => step.depth))
    : contentDepth + 1;
  if (!Number.isFinite(frameDepth)) return [];
  const frames: PresentationStep[] = [];
  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index]!;
    if (step.depth !== frameDepth || step.concurrent || !STORYBOARD_KINDS.has(step.kind)) continue;
    let companionCount = 0;
    for (let cursor = index + 1; cursor < steps.length; cursor += 1) {
      const candidate = steps[cursor]!;
      if (candidate.depth !== step.depth || !candidate.concurrent) break;
      companionCount += 1;
    }
    frames.push(companionCount ? { ...step, companionCount } : step);
  }
  return frames;
}

function sectionKindFor(frame: PresentationStep, fallback: StoryboardSectionKind = "scene"): StoryboardSectionKind {
  switch (frame.kind) {
    case "bg":
      return "scene";
    case "choice":
      return "choice";
    case "cg":
      return "moment";
    case "phone":
    case "trick":
    case "minigame":
      return "interaction";
    case "dialogue":
    case "narration":
      return "dialogue";
    default:
      return fallback;
  }
}

const SECTION_BOUNDARY_KINDS = new Set<LsBlockKind>(["bg", "choice", "cg", "phone", "trick", "minigame"]);
const STANDALONE_SECTION_KINDS = new Set<StoryboardSectionKind>(["choice", "moment", "interaction"]);
const SUPPORTING_SECTION_KINDS = new Set<LsBlockKind>(["char", "music", "sfx", "pause"]);

/**
 * Derive PowerPoint-like sections around one editable frame timeline. Sections
 * are navigation/grouping metadata only: `.ls` and the frame playhead remain
 * the sole source and interaction axis.
 */
export function buildStoryboardSections(frames: readonly PresentationStep[]): StoryboardSection[] {
  const groups: Array<{ kind: StoryboardSectionKind; frames: PresentationStep[] }> = [];
  for (const frame of frames) {
    const current = groups.at(-1);
    const explicitBoundary = SECTION_BOUNDARY_KINDS.has(frame.kind);
    const afterStandalone = current ? STANDALONE_SECTION_KINDS.has(current.kind) : false;
    const startsDialogueAfterStandalone = afterStandalone && (frame.kind === "dialogue" || frame.kind === "narration");
    if (!current || explicitBoundary || startsDialogueAfterStandalone) {
      groups.push({ kind: sectionKindFor(frame), frames: [frame] });
    } else {
      current.frames.push(frame);
    }
  }
  return groups.map((group) => {
    const first = group.frames[0]!;
    const last = group.frames.at(-1)!;
    const firstSubstantive = group.frames.find((frame) => !SUPPORTING_SECTION_KINDS.has(frame.kind));
    const kind = first.kind === "bg" ? group.kind : sectionKindFor(firstSubstantive ?? first, group.kind);
    return {
      id: `${first.id}:section`,
      kind,
      label: SECTION_LABELS[kind],
      summary: first.summary,
      startLine: first.line,
      endLine: last.line,
      frameCount: group.frames.length,
      frames: group.frames,
    };
  });
}

const BRANCH_OUTCOME_KINDS = new Set<LsBlockKind>([
  "bg", "char", "dialogue", "narration", "cg", "music", "sfx", "pause",
  "affection", "signal", "trick", "phoneText", "minigame", "butterfly",
]);

function optionOutcomeGroups(steps: readonly PresentationStep[], optionIndex: number): string[] {
  const option = steps[optionIndex]!;
  const conditions = new Map<number, string | null>();
  const groups: Array<{ key: string; summaries: string[] }> = [];
  for (let index = optionIndex + 1; index < steps.length; index += 1) {
    const outcome = steps[index]!;
    if (outcome.depth <= option.depth) break;
    for (const depth of [...conditions.keys()]) {
      if (depth >= outcome.depth) conditions.delete(depth);
    }
    if (outcome.block.kind === "if") {
      conditions.set(outcome.depth, outcome.block.condition ?? null);
      continue;
    }
    if (!BRANCH_OUTCOME_KINDS.has(outcome.kind)) continue;
    const conditionDepth = Math.max(-1, ...[...conditions.keys()].filter((depth) => depth < outcome.depth));
    const condition = conditionDepth >= 0 ? conditions.get(conditionDepth) : undefined;
    const key = condition === undefined ? "always" : condition === null ? "otherwise" : `if ${condition}`;
    let group = groups.find((candidate) => candidate.key === key);
    if (!group) {
      group = { key, summaries: [] };
      groups.push(group);
    }
    if (group.summaries.length < 3) group.summaries.push(outcome.summary);
  }
  return groups.slice(0, 3).map((group) => {
    const summary = group.summaries.join(" · ");
    return group.key === "always" ? summary : `${group.key} → ${summary}`;
  });
}

function optionHasCheckDependentOutcomes(steps: readonly PresentationStep[], optionIndex: number): boolean {
  const option = steps[optionIndex]!;
  for (let index = optionIndex + 1; index < steps.length; index += 1) {
    const descendant = steps[index]!;
    if (descendant.depth <= option.depth) break;
    if (
      descendant.block.kind === "if" &&
      /\bcheck\.(?:success|fail|any)\b/.test(descendant.block.condition ?? "")
    ) return true;
  }
  return false;
}

function episodeExitRules(steps: readonly PresentationStep[]): StoryboardExitRule[] {
  const gateIndex = steps.findIndex((step) => step.kind === "gate");
  if (gateIndex < 0) return [];
  const gate = steps[gateIndex]!;
  if (gate.block.kind !== "gate") return [];
  if (gate.block.next || gate.block.end) {
    return [{
      line: gate.line,
      condition: null,
      action: gate.block.next ? "next" : "end",
      target: gate.block.next ?? gate.block.end ?? "",
    }];
  }

  const rules: StoryboardExitRule[] = [];
  for (let index = gateIndex + 1; index < steps.length; index += 1) {
    const step = steps[index]!;
    if (step.depth <= gate.depth) break;
    if (step.depth !== gate.depth + 1 || step.block.kind !== "if") continue;
    const target = step.block.next ?? step.block.end;
    if (!target) continue;
    rules.push({
      line: step.line,
      condition: step.block.condition ?? null,
      action: step.block.next ? "next" : "end",
      target,
    });
  }
  return rules;
}

/**
 * Project nested choice syntax into an editor-only branch map. Local option
 * effects and the tail Gate stay separate so the UI never invents a direct
 * option-to-route relationship that does not exist in LunaScript.
 */
export function buildStoryboardBranches(steps: readonly PresentationStep[]): StoryboardBranch[] {
  const exits = episodeExitRules(steps);
  const branches: StoryboardBranch[] = [];
  for (let choiceIndex = 0; choiceIndex < steps.length; choiceIndex += 1) {
    const choice = steps[choiceIndex]!;
    if (choice.kind !== "choice") continue;
    const options: StoryboardBranchOption[] = [];
    for (let index = choiceIndex + 1; index < steps.length; index += 1) {
      const step = steps[index]!;
      if (step.depth <= choice.depth) break;
      if (step.depth !== choice.depth + 1 || step.block.kind !== "option") continue;
      const outcomes = optionOutcomeGroups(steps, index);
      const hasCheckDependentOutcomes = optionHasCheckDependentOutcomes(steps, index);
      options.push({
        line: step.line,
        id: step.block.id ?? String.fromCharCode(65 + options.length),
        mode: step.block.mode ?? "safe",
        text: step.block.text ?? step.summary,
        outcomes,
        hasCheckDependentOutcomes,
      });
    }
    branches.push({ choiceLine: choice.line, options, exits });
  }
  return branches;
}

/**
 * Top-level state and route directives live beside the visual timeline instead
 * of pretending to be player-visible frames. They remain selectable/editable.
 */
export function buildStoryboardTools(steps: readonly PresentationStep[]): PresentationStep[] {
  const episodeDepth = steps.find((step) => step.kind === "episode")?.depth;
  const frameDepth = episodeDepth == null
    ? Math.min(...steps.filter((step) => step.kind !== "episode").map((step) => step.depth))
    : episodeDepth + 1;
  if (!Number.isFinite(frameDepth)) return [];
  return steps.filter((step) => (
    step.depth === frameDepth
    && !step.concurrent
    && step.kind !== "episode"
    && !STORYBOARD_KINDS.has(step.kind)
  ));
}

/** The last `&` follower line folded into a frame, used by the stage preview. */
export function storyboardPreviewLine(steps: readonly PresentationStep[], frameLine: number | null): number | null {
  if (frameLine == null) return null;
  const index = steps.findIndex((step) => step.line === frameLine);
  const frame = steps[index];
  if (!frame) return frameLine;
  let line = frameLine;
  for (let cursor = index + 1; cursor < steps.length; cursor += 1) {
    const candidate = steps[cursor]!;
    if (candidate.depth !== frame.depth || !candidate.concurrent) break;
    line = candidate.line;
  }
  return line;
}

/** First content beat for a newly-loaded episode; structural headers are fallback only. */
export function defaultPresentationSelection(steps: readonly PresentationStep[]): number | null {
  return steps.find((step) => step.kind !== "episode" && step.kind !== "gate")?.line
    ?? steps[0]?.line
    ?? null;
}

/**
 * Keep a selection stable across line-oriented edits. An inserted line wins,
 * then an unchanged selected line, then the closest preceding visible step.
 */
export function repairPresentationSelection(
  steps: readonly PresentationStep[],
  selectedLine: number | null | undefined,
  preferredLine?: number | null,
): number | null {
  if (steps.length === 0) return null;

  for (const candidate of [preferredLine, selectedLine]) {
    if (candidate != null && steps.some((step) => step.line === candidate)) return candidate;
  }

  const target = preferredLine ?? selectedLine ?? steps[0]!.line;
  let previous: PresentationStep | undefined;
  for (const step of steps) {
    if (step.line > target) break;
    previous = step;
  }
  return previous?.line ?? steps[0]!.line;
}
